export interface HubspotFormContext {
  /**
   * Hubspot cookie id
   */
  hutk?: string;
  /**
   * Page URI where the form is rendered
   */
  pageUri?: string;
  /**
   * Page name where the form is rendered
   */
  pageName?: string;
}

export interface HubspotField {
  /**
   * Either 0-1 for contact properties and 0-2 for company properties
   * see hubspot documentation and the associated forms
   */
  objectTypeId: '0-1' | '0-2';
  /**
   * Field name
   */
  name: string;
  /**
   * Field value
   */
  value: string;
}

export type FormData = Record<string, unknown>;

export interface LegalConsentOptions {
  /**
   * The legal consent field to look for in form data
   * If set to empty string, tacit consent is assumed
   */
  field: string;
  /**
   * The legal consent text displayed to the user
   */
  text: string;
  /**
   * Subscription type id, in case of a marketing consent
   */
  id?: string | number;
}

export interface LegalConsent {
  /**
   * Mandatory store and process consent settings
   */
  storeAndProcess: LegalConsentOptions;
  /**
   * Optional marketing process consents settings
   */
  marketing?: LegalConsentOptions[];
}

export interface HubspotLegalConsent {
  consent: {
    consentToProcess: boolean;
    text: string;
    communications:
      | {
          value: boolean;
          subscriptionTypeId: string | number | undefined;
          text: string;
        }[];
  };
}

export interface UseHubspotFormReturn {
  /**
   * Hubspot response data
   */
  data: Ref<unknown>;
  /**
   * Loading state
   */
  loading: Ref<boolean>;
  /**
   * Fetch success state
   */
  success: Ref<boolean>;
  /**
   * Fetch error
   */
  error: Ref<unknown>;
  /**
   * Submit the form to hubspot with data and consent settings
   */
  submitForm: (
    formId: MaybeRef<string>,
    formData: MaybeRef<FormData>,
    consent: MaybeRef<LegalConsent>
  ) => Promise<void>;
}

/**
 * Get consent fields from legal consent settings
 * @param consent - Legal consent settings
 */
function getConsentFields(consent: LegalConsent): string[] {
  const marketingFields = (consent.marketing ?? [])
    .map(({ field }) => field)
    .filter(field => !!field);
  return [consent.storeAndProcess.field, ...marketingFields];
}

/**
 * Format form values to hubspot required format
 * @param formData - Data of the form to transform
 * @param consent - Legal consent settings
 * @returns Form data in hubspot required format
 */
function formatFormFields(
  formData: MaybeRef<FormData>,
  consent: MaybeRef<LegalConsent>,
  lang: MaybeRef<string>
): HubspotField[] {
  const rawFormData = unref(formData);
  const rawConsent = unref(consent);
  const fields = Object.keys(rawFormData).filter(
    // Exclude consent fields, because they are treated differently
    field => !getConsentFields(rawConsent).includes(field)
  );
  return (
    fields
      .map((field: string): HubspotField => {
        return {
          objectTypeId: '0-1',
          name: field,
          value: String(rawFormData[field]),
        };
      })
      // Always add the current lang to the form
      .concat([
        {
          objectTypeId: '0-1',
          name: 'hs_language',
          value: unref(lang),
        },
      ])
  );
}

/**
 * Format form values to consent data for hubspot
 * @param formData - Data of the form to transform
 * @param consent - Legal consent settings
 * @returns Consent data in hubspot required format
 */
function formatLegalConsent(
  formData: MaybeRef<FormData>,
  consent: MaybeRef<LegalConsent>
): HubspotLegalConsent {
  const rawFormData = unref(formData);
  const rawConsent = unref(consent);
  return {
    consent: {
      consentToProcess: Boolean(rawFormData[rawConsent.storeAndProcess.field]),
      text: rawConsent.storeAndProcess.text,
      communications: (rawConsent.marketing ?? []).map(
        ({ field, text, id }) => ({
          // Tacit consent is assumed when field is empty string
          value: field === '' ? true : Boolean(rawFormData[field]),
          subscriptionTypeId: id,
          text,
        })
      ),
    },
  };
}

/**
 * Post a form to the Hubspot API
 * @param formId - Hubspot form guid
 * @returns Composable functionalities and references
 */
export function useHubspotForm(): UseHubspotFormReturn {
  const { locale } = useI18n();

  // Instanciate fetch references right away
  const data = ref<unknown>();
  const error = ref<unknown>(null);
  const success = ref(false);
  const loading = ref(false);

  // Get page context in client side. Context is valid for hubspot only if hubspotutk cookie is set
  const hubspotutkCookie = useCookie('hubspotutk').value;
  const context: HubspotFormContext =
    process.env.NODE_ENV === 'production' &&
    import.meta.client &&
    hubspotutkCookie
      ? {
          hutk: hubspotutkCookie,
          pageUri: window.location.host + window.location.pathname,
          pageName: document.title,
        }
      : {};

  async function submitForm(
    formId: MaybeRef<string>,
    formData: MaybeRef<FormData>,
    consent: MaybeRef<LegalConsent>
  ): Promise<void> {
    loading.value = true;
    const { data, error } = await useFetch(`/api/forms?id=${unref(formId)}`, {
      method: 'POST',
      body: {
        context,
        fields: formatFormFields(formData, consent, locale),
        legalConsentOptions: formatLegalConsent(formData, consent),
      },
    });
    loading.value = false;
    data.value = data.value;
    success.value = !unref(error);
    error.value = unref(error);
  }

  return {
    data,
    loading,
    success,
    error,
    submitForm,
  };
}
